import { useEffect, useRef, useState } from "react";
import { pick, useT } from "../i18n/index.js";
import { Panel } from "../ui/Panel.js";
import { Button } from "../ui/Button.js";
import { useBalances } from "../state/balances.js";
import { CosmeticModelPreview, CosmeticPreviewProvider } from "./CosmeticModelPreview.js";
import { NonModelCosmeticPreview } from "./NonModelCosmeticPreview.js";
import { useStore } from "./state.js";
import { cosmeticsApi } from "./api.js";
import { Price } from "./cards.js";
import type { CosmeticView } from "./types.js";

const KINDS = [
  "building_skin",
  "unit_skin",
  "base_skin",
  "frame",
  "name_colour",
  "chat_bubble",
  "emote_pack",
  "march_trail",
  "stinger",
] as const;

function SkinPreview({ cosmetic, animate = false }: { cosmetic: CosmeticView; animate?: boolean }) {
  if (!cosmetic.asset)
    return <NonModelCosmeticPreview cosmetic={cosmetic} />;
  return <CosmeticModelPreview cosmetic={cosmetic} animate={animate} />;
}

/** Cosmetics: buy, preview and equip one per slot (R58). */
export function CosmeticsView() {
  const t = useT();
  const cosmetics = useStore((s) => s.cosmetics);
  const refreshCosmetics = useStore((s) => s.refreshCosmetics);
  const openConfirm = useStore((s) => s.openConfirm);
  const catalogue = useStore((s) => s.view);
  const run = useStore((s) => s.run);
  const credits = useBalances((s) => s.balances?.credits ?? 0);
  const [kind, setKind] = useState<(typeof KINDS)[number]>("building_skin");
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  useEffect(() => {
    void refreshCosmetics();
  }, [refreshCosmetics]);
  if (!cosmetics) return <p className="hint">{t("common.loading")}</p>;
  const list = cosmetics.filter((c) => c.kind === kind);
  const current = list.find((c) => c.id === selected) ?? list[0] ?? null;
  const transact = async (operation: () => Promise<unknown>) => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    try {
      await operation();
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  const equip = async (c: CosmeticView) => {
    if (
      await run(
        () => (c.equipped ? cosmeticsApi.unequip(c.id) : cosmeticsApi.equip(c.id)),
        c.equipped ? t("store.cos.unequipped") : t("store.cos.equipped"),
      )
    )
      void refreshCosmetics();
  };
  return (
    <CosmeticPreviewProvider>
      <Panel title={t("store.tab.cosmetics")}>
        <div className="row tabs">
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              className={`tab ${k === kind ? "active" : ""}`}
              onClick={() => setKind(k)}
              data-testid={`cos-kind-${k}`}
            >
              {t(`store.cos.kind.${k}`)}
            </button>
          ))}
        </div>
        <div className="cos-layout">
          <div className="cos-preview glass">
            {current ? (
              <>
                <SkinPreview key={current.id} cosmetic={current} animate />
                <div className="offer-name" style={{ marginTop: 6 }}>
                  {pick(current.name)}
                </div>
                {current.bonus ? (
                  <div className="badge gold">
                    +{current.bonus.pct}% {t(`store.cos.bonus.${current.bonus.stat}`)}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
          <div className="cos-list" data-testid="cos-list">
            {list.map((c) => (
              <article
                key={c.id}
                className={`card cos-row ${c.id === current?.id ? "active" : ""}`}
                data-testid={`cos-${c.id}`}
              >
                <SkinPreview cosmetic={c} />
                <button
                  type="button"
                  className="cos-select"
                  aria-pressed={c.id === current?.id}
                  onClick={() => setSelected(c.id)}
                >
                  <span className="cos-name">{pick(c.name)}</span>
                </button>
                {c.bonus ? (
                  <div className="badge gold">
                    +{c.bonus.pct}% {t(`store.cos.bonus.${c.bonus.stat}`)}
                  </div>
                ) : null}
                {c.owned ? (
                  <Button
                    disabled={busy}
                    variant={c.equipped ? "ghost" : "gold"}
                    onClick={() => void transact(() => equip(c))}
                    data-testid={`cos-equip-${c.id}`}
                  >
                    {c.equipped ? t("store.cos.unequip") : t("store.cos.equip")}
                  </Button>
                ) : c.price > 0 ? (
                  <Button
                    variant="primary"
                    disabled={
                      busy || credits < c.price || !catalogue?.cosmetics.some((card) => card.key === c.id)
                    }
                    onClick={() =>
                      openConfirm(catalogue?.cosmetics.find((card) => card.key === c.id) ?? null)
                    }
                    data-testid={`cos-buy-${c.id}`}
                  >
                    <Price price={c.price} currency="CREDITS" />
                  </Button>
                ) : (
                  <span className="dim" style={{ fontSize: 11 }}>
                    {t("store.cos.reward")}
                  </span>
                )}
              </article>
            ))}
          </div>
        </div>
      </Panel>
    </CosmeticPreviewProvider>
  );
}
